import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ChevronRight } from "lucide-react";

const WHATSAPP_NUMBER = "919722855555";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  "FMCG Product Enquiry",
  "Get Export Pricing",
  "Private Label Enquiry",
  "Bulk / Container Order",
];

const FIELDS = [
  { key: "name", label: "Name", placeholder: "Your full name", required: true },
  { key: "company", label: "Company Name", placeholder: "Your company" },
  { key: "country", label: "Country", placeholder: "Destination country", required: true },
  { key: "product", label: "Product Required", placeholder: "e.g. snacks, staples, personal care", required: true },
  { key: "quantity", label: "Estimated Quantity", placeholder: "e.g. 1 x 20ft container" },
  { key: "email", label: "Email", type: "email", placeholder: "you@company.com", required: true },
  { key: "whatsapp", label: "WhatsApp Number", placeholder: "With country code" },
  { key: "requirements", label: "Additional Requirements", placeholder: "Packaging, labelling, target price…", textarea: true },
];

const INPUT_STYLE =
  "w-full border-b border-slate-300 bg-transparent px-0 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-600 focus:outline-none transition-colors duration-300";

const WhatsAppGlyph = ({ size = 22, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const openWhatsApp = (message) =>
  window.open(
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
    "_blank",
    "noopener,noreferrer"
  );

export default function WhatsAppWidget() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("menu");
  const [topic, setTopic] = useState("");
  const [form, setForm] = useState({});
  const [missing, setMissing] = useState([]);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const pickTopic = (opt) => {
    setTopic(opt);
    setStep("form");
  };

  const directChat = () =>
    openWhatsApp(
      "Hello RBL Exim,\nI would like to discuss an FMCG export enquiry.\nThank you."
    );

  const review = (e) => {
    e.preventDefault();
    const miss = FIELDS.filter((f) => f.required && !form[f.key]?.trim()).map(
      (f) => f.key
    );
    setMissing(miss);
    if (miss.length === 0) setStep("ready");
  };

  const send = () => {
    const v = (k) => form[k]?.trim() || "—";
    const message = `Hello RBL Exim,
I am interested in your FMCG export products.

Name: ${v("name")}
Company: ${v("company")}
Country: ${v("country")}
Product Required: ${v("product")}
Estimated Quantity: ${v("quantity")}
Email: ${v("email")}
WhatsApp: ${v("whatsapp")}
Additional Requirements: ${v("requirements")}

Please share product availability, pricing, MOQ and export details.
Thank you.`;

    axios
      .post(`${API}/enquiries`, {
        name: v("name"),
        company: v("company") === "—" ? "Not provided" : v("company"),
        email: v("email"),
        country: v("country"),
        product_interest: `${topic} — ${v("product")}`,
        message: `Via WhatsApp widget.\nEstimated Quantity: ${v("quantity")}\nWhatsApp: ${v("whatsapp")}\nAdditional Requirements: ${v("requirements")}`,
      })
      .catch(() => {});

    openWhatsApp(message);
  };

  const reset = () => {
    setStep("menu");
    setTopic("");
    setForm({});
    setMissing([]);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="whatsapp-fab"
        aria-label="Chat with RBL Exim on WhatsApp"
        className="fixed bottom-20 left-5 z-[70] flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl shadow-emerald-600/30 transition-transform duration-300 hover:scale-105 sm:bottom-6 sm:left-6"
      >
        {open ? <X size={30} /> : <WhatsAppGlyph size={36} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="whatsapp-panel"
            className="fixed bottom-36 left-5 z-[70] flex max-h-[70vh] w-[calc(100vw-3rem)] max-w-sm flex-col overflow-hidden border border-slate-200 bg-white shadow-2xl shadow-slate-900/15 sm:bottom-28 sm:left-6"
          >
            <div className="flex items-center gap-3 bg-slate-900 px-5 py-4 text-white">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]">
                <WhatsAppGlyph size={20} />
              </span>
              <div className="flex-1">
                <p className="font-display text-base font-semibold tracking-tight">
                  RBL Exim
                </p>
                <p className="text-xs text-slate-300">
                  FMCG export enquiries — replies in business hours
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="whatsapp-close-button"
                aria-label="Close chat"
                className="text-slate-400 transition-colors duration-300 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div
              data-lenis-prevent
              className="flex-1 overflow-y-auto bg-sky-50 p-5"
            >
              {step === "menu" && (
                <>
                  <div
                    data-testid="whatsapp-welcome"
                    className="max-w-[85%] border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-800"
                  >
                    Welcome to RBL Exim 👋
                    <br />
                    How can we help you today?
                  </div>
                  <div className="mt-5 space-y-2.5">
                    {OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => pickTopic(opt)}
                        data-testid={`whatsapp-option-${opt.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group flex w-full items-center justify-between border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-800 transition-colors duration-300 hover:border-sky-600 hover:text-sky-700"
                      >
                        {opt}
                        <ChevronRight
                          size={16}
                          className="text-slate-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:text-sky-600"
                        />
                      </button>
                    ))}
                    <button
                      onClick={directChat}
                      data-testid="whatsapp-option-direct-chat"
                      className="flex w-full items-center justify-center gap-2 border border-[#25D366] bg-white px-4 py-3 text-sm font-semibold text-[#128C4A] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
                    >
                      <WhatsAppGlyph size={16} />
                      Talk to RBL Exim on WhatsApp
                    </button>
                  </div>
                </>
              )}

              {step === "form" && (
                <form onSubmit={review} data-testid="whatsapp-lead-form">
                  <button
                    type="button"
                    onClick={() => setStep("menu")}
                    data-testid="whatsapp-back-button"
                    className="mb-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-300 hover:text-sky-700"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <p className="mb-5 border border-sky-200 bg-white p-3 text-sm font-medium text-sky-900">
                    {topic}
                  </p>
                  <div className="space-y-4">
                    {FIELDS.map((f) => (
                      <div key={f.key}>
                        <label
                          htmlFor={`wa-${f.key}`}
                          className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500"
                        >
                          {f.label}
                          {f.required && <span className="text-sky-700"> *</span>}
                        </label>
                        {f.textarea ? (
                          <textarea
                            id={`wa-${f.key}`}
                            data-testid={`whatsapp-field-${f.key}`}
                            rows={2}
                            value={form[f.key] || ""}
                            onChange={set(f.key)}
                            placeholder={f.placeholder}
                            className={`${INPUT_STYLE} resize-none ${
                              missing.includes(f.key) ? "border-red-400" : ""
                            }`}
                          />
                        ) : (
                          <input
                            id={`wa-${f.key}`}
                            data-testid={`whatsapp-field-${f.key}`}
                            type={f.type || "text"}
                            value={form[f.key] || ""}
                            onChange={set(f.key)}
                            placeholder={f.placeholder}
                            className={`${INPUT_STYLE} ${
                              missing.includes(f.key) ? "border-red-400" : ""
                            }`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  {missing.length > 0 && (
                    <p
                      data-testid="whatsapp-form-error"
                      className="mt-4 text-xs font-medium text-red-500"
                    >
                      Please fill in the highlighted fields.
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="whatsapp-review-button"
                    className="mt-6 w-full bg-sky-700 py-3.5 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-slate-900"
                  >
                    Review enquiry
                  </button>
                </form>
              )}

              {step === "ready" && (
                <div>
                  <div
                    data-testid="whatsapp-summary"
                    className="border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-800"
                  >
                    <p className="font-semibold text-slate-900">{topic}</p>
                    <p className="mt-2">
                      {form.name} — {form.company || "Individual buyer"}
                    </p>
                    <p className="text-slate-600">
                      {form.product} · {form.country}
                    </p>
                  </div>
                  <button
                    onClick={send}
                    data-testid="whatsapp-send-button"
                    className="mt-5 flex w-full items-center justify-center gap-2.5 bg-[#25D366] py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-[#1eb857]"
                  >
                    <WhatsAppGlyph size={18} />
                    📱 Send Enquiry on WhatsApp
                  </button>
                  <button
                    onClick={reset}
                    data-testid="whatsapp-start-over"
                    className="mt-3 w-full text-center text-xs font-medium text-slate-500 transition-colors duration-300 hover:text-sky-700"
                  >
                    Start over
                  </button>
                </div>
              )}
            </div>

            <p className="border-t border-slate-200 bg-white px-5 py-3 text-center text-[11px] text-slate-400">
              Opens WhatsApp on mobile, WhatsApp Web on desktop · +91 97228 55555
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
